import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

import { useToast } from '../../../common/hooks/useToast';
import { describeError } from '../../../common/rateLimit';
import { Item } from '../../../styles/common';
import { CrmPageFrame } from '../CrmPageFrame';
import { CRM_PATHS, emailListPath } from '../crmPaths';
import { count, formatDay, isStale, sinceText } from '../manage/manage';
import { EmptyState, LoadingRows, StatTile, StatTiles, TABLE_SX, visuallyHidden } from '../manage/ManageParts';
import { forgetContactData } from '../tagging/tagging';
import { DeleteTagDialog } from './DeleteTagDialog';
import { TagChip, TagEditorDialog } from './TagEditorDialog';
import {
  categoriesOf,
  DEFAULT_VIEW,
  ManagedTag,
  NO_CATEGORY,
  parseTagView,
  SORTS,
  TagShow,
  tagStats,
  TagView,
  tagViewParams,
  visibleTags,
} from './tagManager';
import { savedFilterForTag } from './emailTag';
import { CRM_TAG_MANAGER } from './tags.queries';

const TILES: Array<{ show: TagShow; label: string; hint: string; icon: React.ReactNode; tone: string }> = [
  { show: 'all', label: 'Tags', hint: 'Every tag in the CRM', icon: <LocalOfferOutlinedIcon />, tone: '#1565c0' },
  { show: 'unused', label: 'On nobody', hint: 'Carried by no contact', icon: <PersonOffOutlinedIcon />, tone: '#ef6c00' },
  { show: 'stale', label: 'Not used in a year', hint: 'Given to nobody since', icon: <HistoryOutlinedIcon />, tone: '#ad1457' },
  { show: 'system', label: 'System', hint: 'Set by automation', icon: <LockOutlinedIcon />, tone: '#546e7a' },
];

const PAGE_SIZES = [25, 50, 100];

const NONE = (
  <Box component="span" sx={{ color: '#cbd5e1' }}>
    —
  </Box>
);

/**
 * The tag manager (plan 4.2): every tag with its category, colour, how many
 * carry it and when it was last given, so dead tags are easy to spot. Make a
 * tag, change one, delete one; open a tag's people on the contact list.
 *
 * Every tag arrives in one request (the API does not page them), so search,
 * filtering, sorting and paging happen here, with the view kept in the URL.
 */
export const CrmTags = () => {
  const [params, setParams] = useSearchParams();
  // Fixed for the visit, so "not used in a year" does not move under the admin.
  const [today] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [editing, setEditing] = useState<ManagedTag | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ManagedTag | null>(null);
  const [emailing, setEmailing] = useState<number | null>(null);
  const navigate = useNavigate();

  const view = parseTagView(params);
  const show = (next: TagView) => {
    setParams(tagViewParams(next), { replace: true });
    setPage(0);
  };

  const client = useApolloClient();
  const toast = useToast();
  const { data, loading, error, refetch } = useQuery<{ crmTags: Array<ManagedTag> }>(CRM_TAG_MANAGER, {
    notifyOnNetworkStatusChange: true,
  });

  const tags = data?.crmTags;
  const stats = tags ? tagStats(tags, today) : undefined;
  const categories = useMemo(() => (tags ? categoriesOf(tags) : []), [tags]);
  const visible = useMemo(
    () => (tags ? visibleTags(tags, view, today) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags, view.search, view.category, view.show, view.sort, today],
  );
  const lastPage = Math.max(0, Math.ceil(visible.length / pageSize) - 1);
  const shownPage = Math.min(page, lastPage);
  const rows = visible.slice(shownPage * pageSize, (shownPage + 1) * pageSize);
  const filtered = view.search !== '' || view.category !== null || view.show !== 'all';

  const saved = (tag: ManagedTag, made: boolean) => {
    setEditing(null);
    if (made) {
      // Nobody carries a new tag: only the tag lists are out of date.
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'crmTags' });
      client.cache.gc();
      toast.success(`Created the tag ${tag.name}.`);
    } else {
      // A name or colour shows on every contact carrying it.
      forgetContactData(client.cache, 'selection');
      toast.success(`Saved ${tag.name}.`);
    }
  };

  const deleted = (tag: ManagedTag) => {
    setDeleting(null);
    forgetContactData(client.cache, 'selection');
    toast.success(`Deleted the tag ${tag.name}.`);
  };

  /** Starts a campaign to everyone with the tag, through its saved filter. */
  const emailTag = async (tag: ManagedTag) => {
    setEmailing(tag.id);
    try {
      const listId = await savedFilterForTag(client, tag);
      navigate(emailListPath(listId));
    } catch (err) {
      setEmailing(null);
      toast.error(describeError(err, `Could not start an email to ${tag.name}.`));
    }
  };

  const newTag = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
      New tag
    </Button>
  );

  return (
    <CrmPageFrame
      title="Tags"
      description="Every tag with how many people carry it and when it was last given, so unused tags are easy to spot."
      actions={newTag}
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        <StatTiles label="Show">
          {TILES.map((tile) => (
            <StatTile
              key={tile.show}
              label={tile.label}
              hint={tile.hint}
              icon={tile.icon}
              tone={tile.tone}
              value={stats?.[tile.show]}
              selected={view.show === tile.show}
              onClick={() => show({ ...view, show: view.show === tile.show && tile.show !== 'all' ? 'all' : tile.show })}
            />
          ))}
        </StatTiles>

        <Item sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'grid', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <TextField
                value={view.search}
                onChange={(event) => show({ ...view, search: event.target.value })}
                placeholder="Search by name, category or description"
                size="small"
                sx={{ flex: '1 1 280px' }}
                inputProps={{ 'aria-label': 'Search tags' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: view.search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => show({ ...view, search: '' })}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              <TextField
                select
                label="Order"
                value={view.sort}
                onChange={(event) => show({ ...view, sort: event.target.value as TagView['sort'] })}
                size="small"
                sx={{ minWidth: 170 }}
              >
                {SORTS.map((sort) => (
                  <MenuItem key={sort.id} value={sort.id}>
                    {sort.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {categories.length > 0 && (
              <Box role="group" aria-label="Categories" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                <Chip
                  label="Every category"
                  size="small"
                  color={view.category === null ? 'primary' : 'default'}
                  variant={view.category === null ? 'filled' : 'outlined'}
                  aria-pressed={view.category === null}
                  onClick={() => show({ ...view, category: null })}
                />
                {categories.map((category) => {
                  const on = view.category === category.id;
                  return (
                    <Chip
                      key={category.id}
                      label={`${category.label} · ${category.tags}`}
                      size="small"
                      color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      aria-pressed={on}
                      onClick={() => show({ ...view, category: on ? null : category.id })}
                      sx={{ fontStyle: category.id === NO_CATEGORY ? 'italic' : undefined }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          <Divider />

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: { xs: 1.5, sm: 2 },
              py: 1.25,
            }}
          >
            <Typography variant="body2" color="text.secondary" aria-live="polite">
              {tags
                ? filtered
                  ? `Showing ${count(visible.length)} of ${count(tags.length)} tags`
                  : `${count(tags.length)} ${tags.length === 1 ? 'tag' : 'tags'}`
                : loading
                ? 'Loading tags…'
                : ''}
            </Typography>
            {filtered && (
              <Button size="small" onClick={() => show({ ...DEFAULT_VIEW, sort: view.sort })}>
                Clear filters
              </Button>
            )}
          </Box>

          {loading && tags ? <LinearProgress aria-label="Refreshing tags" sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}

          {error && (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => refetch()}>
                  Try again
                </Button>
              }
            >
              {describeError(error, 'Could not load the tags')}
            </Alert>
          )}

          {tags && tags.length === 0 && (
            <EmptyState
              icon={<LocalOfferOutlinedIcon />}
              title="No tags yet"
              text="Tags group people across the CRM: volunteers, donors, a campaign's sign-ups. Make the first one here, or from the contact list."
              action={newTag}
            />
          )}

          {tags && tags.length > 0 && visible.length === 0 && (
            <EmptyState
              icon={<SearchIcon />}
              title="No tags match"
              text="Nothing fits this search and these filters."
              action={
                <Button variant="outlined" onClick={() => show({ ...DEFAULT_VIEW, sort: view.sort })}>
                  Clear filters
                </Button>
              }
            />
          )}

          {(!tags || visible.length > 0) && !(error && !tags) && (
            <TableContainer sx={{ ...TABLE_SX, opacity: loading && tags ? 0.6 : 1, transition: 'opacity 120ms' }}>
              <Table size="small" aria-label="Tags">
                <TableHead>
                  <TableRow>
                    <TableCell>Tag</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">People</TableCell>
                    <TableCell>Last given</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={visuallyHidden}>
                        Actions
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!tags && <LoadingRows columns={6} />}
                  {rows.map((tag) => (
                    <TagRow
                      key={tag.id}
                      tag={tag}
                      today={today}
                      onEdit={() => setEditing(tag)}
                      onDelete={() => setDeleting(tag)}
                      onEmail={() => emailTag(tag)}
                      emailing={emailing === tag.id}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {visible.length > PAGE_SIZES[0] && (
            <>
              <Divider />
              <TablePagination
                component="div"
                count={visible.length}
                page={shownPage}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={pageSize}
                rowsPerPageOptions={PAGE_SIZES}
                onRowsPerPageChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(0);
                }}
              />
            </>
          )}
        </Item>
      </Box>

      {editing && (
        <TagEditorDialog
          tag={editing === 'new' ? undefined : editing}
          categories={categories.filter((c) => c.id !== NO_CATEGORY).map((c) => c.id)}
          onClose={() => setEditing(null)}
          onSaved={saved}
        />
      )}
      {deleting && <DeleteTagDialog tag={deleting} onClose={() => setDeleting(null)} onDeleted={() => deleted(deleting)} />}
    </CrmPageFrame>
  );
};

interface TagRowProps {
  tag: ManagedTag;
  today: string;
  onEdit: () => void;
  onDelete: () => void;
  onEmail: () => void;
  emailing: boolean;
}

const TagRow = ({ tag, today, onEdit, onDelete, onEmail, emailing }: TagRowProps) => (
  <TableRow hover>
    <TableCell sx={{ minWidth: 260, maxWidth: 460 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <TagChip name={tag.name} color={tag.color} />
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
          #{tag.id}
        </Typography>
        {tag.isSystem && (
          <Tooltip title="Set by automation: it keeps its name and cannot be deleted.">
            <Chip
              size="small"
              variant="outlined"
              icon={<LockOutlinedIcon />}
              label="System"
              sx={{ height: 22, fontSize: '0.6875rem', '& .MuiChip-icon': { fontSize: 14 } }}
            />
          </Tooltip>
        )}
      </Box>
      {tag.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          title={tag.description}
          sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {tag.description}
        </Typography>
      )}
    </TableCell>
    <TableCell sx={{ whiteSpace: 'nowrap' }}>{tag.category ?? NONE}</TableCell>
    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
      {tag.contactCount > 0 ? (
        <Tooltip title="See them on the contact list">
          <Link
            component={RouterLink}
            to={`${CRM_PATHS.contacts}?tags=${tag.id}`}
            underline="hover"
            aria-label={`${count(tag.contactCount)} contacts tagged ${tag.name}`}
            sx={{ fontWeight: 600 }}
          >
            {count(tag.contactCount)}
          </Link>
        </Tooltip>
      ) : (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          0
        </Box>
      )}
    </TableCell>
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      {tag.lastAddedAt ? (
        <>
          {formatDay(tag.lastAddedAt)}
          {sinceText(tag.lastAddedAt, today) && (
            <Typography
              variant="caption"
              component="div"
              sx={{ color: isStale(tag.lastAddedAt, today) ? 'warning.dark' : 'text.secondary' }}
            >
              {sinceText(tag.lastAddedAt, today)}
            </Typography>
          )}
        </>
      ) : (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          Never
        </Box>
      )}
    </TableCell>
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      {formatDay(tag.createdAt)}
      {sinceText(tag.createdAt, today) && (
        <Typography variant="caption" color="text.secondary" component="div">
          {sinceText(tag.createdAt, today)}
        </Typography>
      )}
    </TableCell>
    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
      <Tooltip title={tag.contactCount > 0 ? 'Email everyone with this tag' : 'Nobody has this tag'}>
        <span>
          <IconButton size="small" aria-label={`Email people tagged ${tag.name}`} onClick={onEmail} disabled={tag.contactCount === 0 || emailing}>
            {emailing ? <CircularProgress size={16} /> : <SendOutlinedIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton size="small" aria-label={`Edit ${tag.name}`} onClick={onEdit}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tag.isSystem ? 'System tags cannot be deleted' : 'Delete'}>
        <span>
          <IconButton
            size="small"
            aria-label={`Delete ${tag.name}`}
            onClick={onDelete}
            disabled={tag.isSystem}
            sx={{ '&:hover': { color: 'error.main' } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </TableCell>
  </TableRow>
);
